import { useState } from "react";
import { formatPrice } from "@flicket/utils";
import { Divider, Flex, Text, LegacyText, Price, LinkText } from "flicket-ui";
import { FeeType, OrderQuery, TaxCalculationMethod } from "~graphql/sdk";
import { TicketFees } from "./checkout.order.TicketFees";
import { TransactionFee } from "~features/checkout/helpers/getTotals";
import { usePaymentOptionsStore } from "~features/checkout/helpers/paymentOptions.store";
import { useOrganization } from "~hooks";

export type ServiceFeeProps = {
  bookingFeeAmount?: number;
  transferFeeAmount?: number;
  deliveryFee?: number;
  lineItems: OrderQuery["order"]["lineItems"]["edges"][number]["node"][];
  transactionFee?: TransactionFee;
  taxCalculationMethod?: TaxCalculationMethod;
  totalTax: number;
  taxRate: number;
  customTaxAmount?: number;
  paymentCount: number;
  orderCustomTax?: OrderQuery["order"]["tax"];
};

export const transactionFeeDescription = (
  transactionFee: Omit<TransactionFee, "transactionFeeAmount">,
  paymentCount: number,
  currency: string
): string => {
  if (transactionFee.transactionFee2 && transactionFee.transactionFeeType2) {
    if (transactionFee.transactionFee) {
      if (transactionFee.transactionFeeType === FeeType.Percentage) {
        return ` (${transactionFee.transactionFee}% + ${formatPrice(
          transactionFee.transactionFee2 * paymentCount,
          {
            currency,
          }
        )})`;
      } else if (transactionFee.transactionFeeType === FeeType.FlatRate) {
        return ` (${transactionFee.transactionFee2}% + ${formatPrice(
          transactionFee.transactionFee * paymentCount,
          {
            currency,
          }
        )})`;
      }
    } else if (transactionFee.transactionFeeType2 === FeeType.Percentage) {
      return ` (${transactionFee.transactionFee2}%)`;
    }
  }

  return transactionFee.transactionFeeType === FeeType.Percentage &&
    transactionFee.transactionFee !== 0
    ? ` (${transactionFee.transactionFee}%)`
    : ``;
};

export const ServiceFees = ({
  lineItems,
  bookingFeeAmount,
  deliveryFee,
  transactionFee,
  transferFeeAmount,
  taxCalculationMethod = TaxCalculationMethod.Inclusive,
  totalTax,
  taxRate,
  customTaxAmount,
  paymentCount,
  orderCustomTax,
}: ServiceFeeProps) => {
  const paymentPlanState = usePaymentOptionsStore();
  const { organization } = useOrganization();
  const [showDetails, setShowDetails] = useState(false);
  const sum = lineItems.reduce(
    (acc, curr) =>
      acc +
      (curr.ticketFeeAmount + (curr.stadiumLevyFee ?? 0)) *
        (curr?.quantity ?? 1),
    0
  );

  const totalFees =
    sum +
    transactionFee?.transactionFeeAmount +
    deliveryFee +
    bookingFeeAmount +
    transferFeeAmount +
    (orderCustomTax?.totalExclusive ?? 0) +
    (taxCalculationMethod === TaxCalculationMethod.Exclusive ? totalTax : 0) +
    (customTaxAmount ?? 0) +
    (paymentPlanState?.paymentPlanFeeAmount ?? 0);

  // We shouldn't need the dropdown details if no service details
  const hideServiceFeeDetails = totalFees <= 0;

  if (hideServiceFeeDetails) return null;

  const exclusiveTaxes = (orderCustomTax?.tax ?? []).filter(
    (tax) => tax.isExclusive && tax.total > 0
  );

  return (
    <>
      <Flex
        w="100%"
        justifyContent="space-between"
        mt={2}
        className="service-fees"
      >
        <Flex flexDir="column">
          <Flex alignItems="baseline">
            <LegacyText
              fontSize={3}
              lineHeight="normal"
              fontWeight="demiBold"
              color="N800"
              textTransform="capitalize"
            >
              Service costs
            </LegacyText>
            <Text variant="small" ml="1/2">
              <LinkText onClick={() => setShowDetails(!showDetails)}>
                {showDetails ? "Hide" : "Show"} details
              </LinkText>
            </Text>
          </Flex>
        </Flex>
        {!showDetails && (
          <Price
            color="N800"
            fontSize={3}
            lineHeight="normal"
            price={totalFees}
            currency={organization.currency}
          />
        )}
      </Flex>
      {showDetails && (
        <>
          <TicketFees lineItems={lineItems} />
          <Flex
            w="100%"
            alignItems="center"
            justifyContent="space-between"
            mt={3}
          >
            <LegacyText
              fontSize={3}
              lineHeight="normal"
              fontWeight="demiBold"
              color="N800"
              textTransform="capitalize"
            >
              Delivery costs
            </LegacyText>
            {!deliveryFee ? (
              <LegacyText color="N800" lineHeight="normal" fontSize={3}>
                {deliveryFee === 0 ? "Free shipping" : "-"}
              </LegacyText>
            ) : (
              <Price
                color="N800"
                fontSize={3}
                lineHeight="normal"
                price={deliveryFee}
                currency={organization.currency}
              />
            )}
          </Flex>
          <Flex w="100%" justifyContent="space-between" mt={3}>
            <Flex flexDir="column">
              <LegacyText
                fontSize={3}
                lineHeight="normal"
                fontWeight="demiBold"
                color="N800"
                textTransform="capitalize"
              >
                Order Handling Fee
              </LegacyText>
            </Flex>
            <Price
              color="N800"
              fontSize={3}
              lineHeight="normal"
              price={bookingFeeAmount}
              currency={organization.currency}
            />
          </Flex>
          {paymentPlanState?.paymentPlanFeeAmount && (
            <Flex w="100%" justifyContent="space-between" mt={3}>
              <Flex flexDir="column">
                <LegacyText
                  fontSize={3}
                  lineHeight="normal"
                  fontWeight="demiBold"
                  color="N800"
                  textTransform="capitalize"
                >
                  Payment Plan Fee
                </LegacyText>
              </Flex>
              <Price
                color="N800"
                fontSize={3}
                lineHeight="normal"
                price={paymentPlanState?.paymentPlanFeeAmount}
                currency={organization.currency}
              />
            </Flex>
          )}
          {exclusiveTaxes.map((taxItem, index) => (
            <Flex key={index} w="100%" justifyContent="space-between" mt={3}>
              <Flex flexDir="column">
                <LegacyText
                  fontSize={3}
                  lineHeight="normal"
                  fontWeight="demiBold"
                  color="N800"
                  textTransform="capitalize"
                >
                  {taxItem.name}
                </LegacyText>
              </Flex>
              <Price
                color="N800"
                fontSize={3}
                lineHeight="normal"
                price={taxItem.total}
                currency={organization.currency}
              />
            </Flex>
          ))}
          {!!transactionFee?.transactionFeeAmount && (
            <Flex w="100%" justifyContent="space-between" mt={3}>
              <Flex flexDir="column">
                <LegacyText
                  fontSize={3}
                  lineHeight="normal"
                  fontWeight="demiBold"
                  color="N800"
                  textTransform="capitalize"
                >
                  Transaction costs
                  <Text
                    variant="small"
                    mt={"1/4"}
                    display={{
                      _: "inline",
                      md: "block",
                    }}
                  >
                    {transactionFeeDescription(
                      transactionFee,
                      paymentCount,
                      organization.currency
                    )}
                  </Text>
                </LegacyText>
              </Flex>
              <Price
                color="N800"
                fontSize={3}
                lineHeight="normal"
                price={transactionFee?.transactionFeeAmount}
                currency={organization.currency}
              />
            </Flex>
          )}
          {!!transferFeeAmount && (
            <Flex w="100%" justifyContent="space-between" mt={3}>
              <Flex flexDir="column">
                <LegacyText
                  fontSize={3}
                  lineHeight="normal"
                  fontWeight="demiBold"
                  color="N800"
                  textTransform="capitalize"
                >
                  Ticket transfer fee
                </LegacyText>
              </Flex>
              <Price
                color="N800"
                fontSize={3}
                lineHeight="normal"
                price={transferFeeAmount}
                currency={organization.currency}
              />
            </Flex>
          )}
          {taxRate > 0 &&
            taxCalculationMethod === TaxCalculationMethod.Exclusive && (
              <Flex w="100%" justifyContent="space-between" mt={3}>
                <Flex flexDir="column">
                  <LegacyText
                    fontSize={3}
                    lineHeight="normal"
                    fontWeight="demiBold"
                    color="N800"
                    textTransform="capitalize"
                  >
                    Sales Tax ({taxRate * 100}%)
                  </LegacyText>
                </Flex>
                <Price
                  color="N800"
                  fontSize={3}
                  lineHeight="normal"
                  price={totalTax}
                  currency={organization.currency}
                />
              </Flex>
            )}
        </>
      )}
      <Divider mt={2} />
    </>
  );
};
